import React from 'react';
import {Formik, Form, Field, ErrorMessage} from 'formik';

// utils
import {Guid} from './utils/guid';
import useUrlGetParams from './utils/useUrlGetParams';

type GameStates = 'start' | 'howManyPeople' | 'collectPeople' | 'playGameSplash' | 'playGame' | 'results';

interface Score {
  personId: string;
  isCorrect: boolean;
}

interface Person {
  id: string;
  name: string;
  fact: string;
  scores: Score[];
}

const samplePeople = [
  {
    id: '11',
    name: 'Chance',
    fact: 'did doughnuts in the school yard',
    scores: [],
  },
  {
    id: '22',
    name: 'Derrick',
    fact: 'likes to smell kettlebells',
    scores: [],
  },
  {
    id: '33',
    name: 'Adam',
    fact: 'remembers names by thinking of ice cream flavors',
    scores: [],
  },
  {
    id: '44',
    name: 'Jill',
    fact: 'used to eat cake out of the trash can',
    scores: [],
  },
  {
    id: '55',
    name: 'William',
    fact: 'ties his shoes the responsible way',
    scores: [],
  },
  {
    id: '66',
    name: 'Brantley',
    fact: 'only takes conference calls in the bathroom',
    scores: [],
  },
  {
    id: '77',
    name: 'Zach',
    fact: 'has only one onomatopoeia',
    scores: [],
  },
];

function App() {
  const usePrefilledPeople = useUrlGetParams().prefilled;

  const [gameView, setGameView] = React.useState<GameStates>('start');

  const personNameInputRef = React.createRef<HTMLInputElement>();

  const [howManyPeople, setHowManyPeople] = React.useState<number>(
    usePrefilledPeople ? parseInt(usePrefilledPeople) : 3,
  );
  const handlePeopleCount = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value) setHowManyPeople(parseInt(event.target.value));
  };

  const [people, setPeople] = React.useState<Person[]>(
    usePrefilledPeople ? samplePeople.splice(0, parseInt(usePrefilledPeople)) : [],
  );

  console.log({people});

  const hasAllPeopleFacts = people.length === howManyPeople;

  const [personsTurn, setPersonsTurn] = React.useState<number>(0);

  console.log({hasAllPeopleFacts});

  React.useEffect(() => {
    if (hasAllPeopleFacts && people[personsTurn].scores.length === 0) setGameView('playGameSplash');
  }, [people, howManyPeople, hasAllPeopleFacts, personsTurn]);

  if (gameView === 'start')
    return (
      <div className="h-100 w-100 bg-light d-flex justify-content-center align-items-center">
        <div className="text-center">
          <h1 className="mb-5">Fun Fact</h1>
          <button className="btn btn-primary" onClick={() => setGameView('howManyPeople')}>
            Start Fun Fact
          </button>
        </div>
      </div>
    );

  if (gameView === 'howManyPeople')
    return (
      <div className="h-100 w-100 bg-light d-flex justify-content-center align-items-center">
        <div className="text-center">
          <h2 className="mb-5">How many are playing?</h2>
          <input type="number" onChange={handlePeopleCount} value={howManyPeople} className="mb-3" autoFocus />
          <p>
            Okay, <strong>{howManyPeople}</strong> players.
          </p>
          <button className="btn btn-primary" onClick={() => setGameView('collectPeople')}>
            Continue
          </button>
        </div>
      </div>
    );

  console.log({people});

  if (gameView === 'collectPeople' && people.length < howManyPeople) {
    return (
      <div className="h-100 w-100 bg-light d-flex justify-content-center align-items-center">
        <div className="text-center">
          {people.length === 0 ? (
            <h2 className="mb-5">Who is the game host?</h2>
          ) : (
            <h2 className="mb-5">Who is player #{people.length + 1}?</h2>
          )}
          <Formik
            initialValues={{name: '', fact: ''}}
            onReset={values => {
              values.name = '';
              values.fact = '';
              // reset focus - source: https://medium.com/@martin_hotell/react-refs-with-typescript-a32d56c4d315
              if (personNameInputRef.current) personNameInputRef.current.focus();
            }}
            onSubmit={(values, {setSubmitting}) => {
              setTimeout(() => {
                setPeople([
                  ...people,
                  {
                    id: Guid.newGuid(),
                    name: values.name,
                    fact: values.fact,
                    scores: [],
                  },
                ]);

                values.name = '';
                values.fact = '';

                if (hasAllPeopleFacts) setGameView('playGameSplash');

                setSubmitting(false);
              }, 400);
            }}>
            {({isSubmitting}) => (
              <Form>
                <div className="field-group d-flex flex-column text-left mb-1">
                  <label htmlFor="name">Name:</label>
                  <Field type="text" name="name" innerRef={personNameInputRef} autoFocus />
                  <ErrorMessage name="name" component="div" />
                </div>
                <div className="field-group d-flex flex-column text-left">
                  <label htmlFor="name">A fun fact about you:</label>
                  <Field type="text" name="fact" placeholder="used to eat toothpaste" />
                  <ErrorMessage name="fact" component="div" />
                </div>
                <button type="submit" disabled={isSubmitting} className="btn btn-primary mt-3">
                  Add me
                </button>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    );
  }

  const filteredPeopleOptions = people.filter(person => person.id !== people[personsTurn].id);
  const filteredPeople = filteredPeopleOptions.filter(
    person => !people[personsTurn].scores.map(score => score.personId).includes(person.id),
  );
  const currentRandomFact = filteredPeople[Math.floor(Math.random() * filteredPeople.length)];

  const handleAddScore = (score: Score) => {
    const updatedPeople = people.map(person => {
      if (person.id === people[personsTurn].id) return {...person, scores: [...person.scores, score]};
      return person;
    });

    setPeople(updatedPeople);
    if (
      personsTurn < updatedPeople.length - 1 &&
      updatedPeople[personsTurn].scores.length === updatedPeople.length - 1
    ) {
      setGameView('playGameSplash');
      setPersonsTurn(personsTurn + 1);
    }
  };

  console.log(
    {filteredPeopleOptions, filteredPeople, currentRandomFact, person: people[personsTurn]},
    people[personsTurn].scores.map(score => score.personId),
  );

  if (currentRandomFact)
    return (
      <div className="h-100 w-100 bg-light d-flex justify-content-center align-items-center">
        {gameView === 'playGameSplash' && (
          <div className="text-center">
            {personsTurn === 0 ? (
              <>
                <h2 className="mb-5">Let the game begin.</h2>
                <p>You're up first, {people[personsTurn].name}! 💪</p>
              </>
            ) : (
              <h2 className="mb-5">It's {people[personsTurn].name}'s turn now! 👉</h2>
            )}
            <button className="btn btn-primary" onClick={() => setGameView('playGame')}>
              Go
            </button>
          </div>
        )}

        {gameView === 'playGame' && (
          <div className="text-center">
            <h3 className="mb-5">👋,{people[personsTurn].name}!</h3>
            <h2>Who {currentRandomFact.fact}?</h2>
            <div className="d-flex flex-column">
              <div>
                {filteredPeopleOptions.map((person: Person) => (
                  <button
                    key={person.id}
                    className="btn btn-primary btn-lg mr-2 mb-2"
                    onClick={() =>
                      handleAddScore({personId: currentRandomFact.id, isCorrect: currentRandomFact.id === person.id})
                    }>
                    {person.name}
                  </button>
                ))}
              </div>
              <p className="text-muted">choose a player</p>
            </div>
          </div>
        )}
      </div>
    );

  return (
    <div className="h-100 w-100 bg-light d-flex justify-content-center align-items-center">
      <div className="text-center">
        <h2>Game Complete</h2>
        <ul className="text-left h6">
          {people
            .map((person: Person) => ({
              name: person.name,
              fact: person.fact,
              correctsCount: person.scores.reduce((acc, score: Score) => {
                if (score.isCorrect) return acc + 1;
                return acc;
              }, 0),
            }))
            .map((person: {name: string; correctsCount: number; fact: string}) => (
              <li className="mb-3">
                <strong>{person.name}</strong> - {person.correctsCount} correct
                <br />
                <div className="text-muted mt-1">"{person.fact}"</div>
              </li>
            ))}
        </ul>
      </div>
    </div>
  );
}

export default App;
